import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Scale, Lock, Mail, AlertCircle, ArrowRight, ArrowLeft, X, ShieldCheck, KeyRound, CreditCard } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { showToast } from '../../components/common/Toast';
import PasswordInput from '../../components/common/PasswordInput';
import SubscriptionModal from '../../components/subscription/SubscriptionModal';

export default function AdminLoginPage() {
  const [step, setStep] = useState(1); // 1: Login & Password, 2: Two-Factor / 2-bosqich
  const [loginIdentifier, setLoginIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [step2Password, setStep2Password] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionModalOpen, setSubscriptionModalOpen] = useState(false);

  const { login, isAuthenticated, isAdmin, isLawyer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/admin/dashboard';

  React.useEffect(() => {
    if (isAuthenticated && (isAdmin || isLawyer)) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, isAdmin, isLawyer, navigate]);

  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    if (!loginIdentifier || !password) return;

    setLoading(true);
    setError(null);

    try {
      const res = await login(loginIdentifier, password);
      if (res?.requiresTwoFactor) {
        setStep(2);
        showToast('1-bosqich tasdiqlandi! 2-bosqich maxfiy parolini kiriting.', 'info');
        return;
      }
      showToast('Xush kelibsiz! Tizimga muvaffaqiyatli kirildi.', 'success');
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || 'Kirishda xatolik yuz berdi');
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStep2 = async (e) => {
    e.preventDefault();
    if (!step2Password) return;

    setLoading(true);
    setError(null);

    try {
      await login(loginIdentifier, password, step2Password);
      showToast('Xush kelibsiz! 2-bosqichli kirish muvaffaqiyatli yakunlandi.', 'success');
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message || '2-bosqich xavfsizlik paroli noto‘g‘ri');
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060c17] flex items-center justify-center p-4 relative">
      {/* Floating Exit X Button */}
      <button
        type="button"
        onClick={() => navigate('/')}
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-50 w-10 h-10 rounded-full bg-[#091322]/90 hover:bg-[#12233b] border-2 border-[#c5a880] flex items-center justify-center text-slate-200 hover:text-white shadow-2xl backdrop-blur-md transition-all cursor-pointer hover:scale-105 active:scale-95"
        title="Bosh sahifaga qaytish (X)"
        aria-label="Yopish"
      >
        <X className="w-5 h-5 text-[#c5a880]" />
      </button>

      <div className="max-w-md w-full bg-[#091322] border border-[#1b2b46] rounded-xl shadow-2xl overflow-hidden p-6 sm:p-8 relative">
        {/* Card Close 'X' Button */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-[#0d1c33] hover:bg-[#152847] border border-slate-700 hover:border-[#c5a880] flex items-center justify-center text-slate-300 hover:text-white transition-colors cursor-pointer"
          title="Yopish va Bosh sahifaga qaytish (X)"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Brand header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded border border-[#c5a880]/50 bg-[#0d1c33] flex items-center justify-center text-[#c5a880] mx-auto mb-3 shadow-md">
            {step === 1 ? <Scale className="w-6 h-6" /> : <ShieldCheck className="w-6 h-6 text-[#c5a880]" />}
          </div>
          <h1 className="font-serif text-xl sm:text-2xl font-bold text-slate-100 tracking-wider">
            JIZZAX ADVOKATLARI
          </h1>
          <p className="text-[11px] uppercase tracking-widest text-[#c5a880] mt-1 font-semibold">
            {step === 1 ? 'Boshqaruv Tizimiga Kirish' : '2-Bosqichli Xavfsizlik Tekshiruvi'}
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3.5 rounded-xl bg-rose-950/60 border border-rose-800 text-rose-200 text-xs flex flex-col space-y-2.5 animate-in fade-in duration-150">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 mr-2 shrink-0 mt-0.5 text-rose-400" />
              <span className="leading-relaxed">{error}</span>
            </div>
            {(error.includes('obuna') || error.includes('to‘lov') || error.includes('ruxsat')) && (
              <button
                type="button"
                onClick={() => setSubscriptionModalOpen(true)}
                className="w-full py-2.5 px-3 rounded-lg bg-gradient-to-r from-[#b38038] to-[#c59b27] hover:to-[#d4af37] text-slate-950 font-bold text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all shadow-md cursor-pointer mt-1"
              >
                <CreditCard className="w-3.5 h-3.5" />
                <span>Oylik Obunani To‘lash (99 000 UZS)</span>
              </button>
            )}
          </div>
        )}

        {/* STEP 1: LOGIN & PASSWORD */}
        {step === 1 && (
          <form onSubmit={handleSubmitStep1} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                Email yoki Login
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="text"
                  value={loginIdentifier}
                  onChange={(e) => setLoginIdentifier(e.target.value)}
                  placeholder="Login yoki emailni kiriting"
                  className="w-full pl-9 pr-3 py-2.5 bg-[#050b14] border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-[#c5a880]"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                Parol
              </label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Parolni kiriting"
                startIcon={<Lock className="w-4 h-4" />}
                className="w-full py-2.5 bg-[#050b14] border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-[#c5a880]"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-[#c5a880] hover:bg-[#d4b993] active:bg-[#b89569] text-[#091322] font-bold rounded text-sm transition-all shadow-md mt-2 flex items-center justify-center cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <span>Tekshirilmoqda...</span>
              ) : (
                <>
                  <span>Davom etish (1-bosqich)</span>
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </button>
          </form>
        )}

        {/* STEP 2: TWO-FACTOR SECURITY VERIFICATION */}
        {step === 2 && (
          <form onSubmit={handleSubmitStep2} className="space-y-4 animate-in fade-in duration-200">
            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs flex items-center space-x-2">
              <KeyRound className="w-4 h-4 shrink-0 text-amber-400" />
              <span>2-bosqich maxfiy xavfsizlik parolini kiriting</span>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
                2-Bosqich Paroli <span className="text-[#c5a880]">*</span>
              </label>
              <PasswordInput
                value={step2Password}
                onChange={(e) => setStep2Password(e.target.value)}
                placeholder="••••••••"
                startIcon={<KeyRound className="w-4 h-4 text-emerald-400" />}
                className="w-full py-2.5 bg-[#050b14] border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-[#c5a880]"
                required
                autoFocus
              />
            </div>

            <div className="flex items-center space-x-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setStep(1);
                  setError(null);
                }}
                className="w-1/3 py-2.5 px-3 bg-[#0d1c33] hover:bg-[#152847] border border-slate-700 text-slate-300 rounded text-xs font-semibold flex items-center justify-center cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                <span>Orqaga</span>
              </button>

              <button
                type="submit"
                disabled={loading}
                className="w-2/3 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded text-xs transition-all shadow-md flex items-center justify-center cursor-pointer disabled:opacity-50"
              >
                {loading ? (
                  <span>Tekshirilmoqda...</span>
                ) : (
                  <>
                    <span>Tasdiqlash va Kirish</span>
                    <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 pt-4 border-t border-slate-800 text-center">
          <Link
            to="/"
            className="text-xs text-slate-400 hover:text-[#c5a880] transition-colors inline-flex items-center"
          >
            ← Bosh sahifaga qaytish
          </Link>
        </div>
      </div>

      <SubscriptionModal
        isOpen={subscriptionModalOpen}
        onClose={() => setSubscriptionModalOpen(false)}
        initialType="LAWYER"
        onSuccess={() => {
          setSubscriptionModalOpen(false);
          showToast('To‘lov cheki yuborildi! Ma’muriyat tasdiqlashini kuting.', 'success');
        }}
      />
    </div>
  );
}
